/**
 * Record workspace (ADR-0118): one page for one record — a criminal case, a person, a payment —
 * set up by the profile's record type. The page loads the record through the type's lookup (an
 * agent request, like the record card) and draws its sections from what the lookup returned:
 * the record's fields, a part of it (its people, timeline, evidence, places, links), or a related
 * lookup's result. Analyses start a named agent with the record's key. Nothing here calls a
 * system directly and nothing is specific to one organisation: a police case file is one
 * configuration.
 *
 * Purpose of access: a record type may ask why a person opens a record (`purpose_required`). The
 * reason is asked once per record per browser session and sent with every request made for that
 * record (run input `purpose`), which the platform records in the audit trail.
 */
import { fillPlaceholders, recordType, type Bi, type CommandProfile, type RecordType } from "./commandProfiles";
import { pick, recordOf, rowsOf, type Row } from "./viewData";
import type { ViewBlock, ViewHints, ViewKind } from "./views";

/** How a section is drawn. Without one it follows the data (a list of places is a map). */
export type SectionKind = "fields" | "people" | "documents" | "table" | "timeline" | "map" | "network" | "list" | "views";

export interface WorkspaceSection {
  id: string;
  label: Bi;
  /** `lookup` (the record itself), `related:<index>` (a related lookup) or `part:<field>` (a part of the record). */
  source: string;
  kind?: SectionKind;
  /** People and table rows: a click opens this record type's workspace with the row's field as its key. */
  open?: { type: string; field: string };
  /** Documents: the call an agent makes to open one (the row's `field`, default `id`, goes into `arg`). */
  document?: { tool: string; action: string; arg: string; agent?: string; field?: string };
}

export interface WorkspaceAnalysis {
  id: string;
  label: Bi;
  agent: string;
  /** Agent input; `{record.key}` and `{record.<field>}` are filled from the record. */
  input?: Record<string, string>;
  /** What the request asks in words (default: the label and the record's key). */
  question?: Bi;
  /** A chat box bound to the agent ("Ask about this case") instead of a button. */
  chat?: boolean;
}

export interface RecordWorkspace {
  sections: WorkspaceSection[];
  analyses?: WorkspaceAnalysis[];
}

export const SECTION_KINDS: readonly SectionKind[] = ["fields", "people", "documents", "table", "timeline", "map", "network", "list", "views"];
const SOURCE = /^(lookup|related:\d{1,2}|part:[A-Za-z_][A-Za-z0-9_.]{0,63})$/;

export function sourceValid(source: string, type: Pick<RecordType, "related">): boolean {
  if (!SOURCE.test(source)) return false;
  if (source.startsWith("related:")) return Number(source.slice(8)) < (type.related?.length ?? 0);
  return true;
}

/**
 * The sections and analyses a record type's workspace shows. A type without `workspace` still
 * gets one: the record's fields, then each related lookup as its own section.
 */
export function workspaceOf(type: RecordType): Required<RecordWorkspace> {
  const ws = type.workspace;
  if (ws?.sections?.length) return { sections: ws.sections.filter((s) => sourceValid(s.source, type)), analyses: ws.analyses ?? [] };
  return {
    sections: [
      { id: "overview", label: { en: "Overview", ar: "نظرة عامة" }, source: "lookup", kind: "fields" },
      ...(type.related ?? []).map((r, i) => ({ id: `related-${i}`, label: r.label, source: `related:${i}`, kind: "views" as const })),
    ],
    analyses: ws?.analyses ?? [],
  };
}

/** The lookups a workspace needs: the record's own, and the related ones its sections show. */
export function neededLookups(ws: Required<RecordWorkspace>): string[] {
  const out = new Set<string>(["lookup"]);
  for (const s of ws.sections) if (s.source.startsWith("related:")) out.add(s.source);
  return [...out];
}

/** What one lookup gave back (the record card's `LookupResult`, reduced to what sections use). */
export interface LookupPart {
  views: ViewBlock[];
  answer: string;
}

/** The lookup's main display: the first one carrying data (the record). */
export function mainView(part: LookupPart | undefined): ViewBlock | null {
  return part?.views.find((v) => v.data !== undefined && v.data !== null) ?? null;
}

/** The record the lookup returned, and the hints it came with. */
export function recordFrom(part: LookupPart | undefined): { record: Row; hints: ViewHints } {
  const v = mainView(part);
  if (!v) return { record: {}, hints: {} };
  return { record: recordOf(v.data, v.hints), hints: v.hints ?? {} };
}

export type SectionData =
  | { kind: "fields"; record: Row; hints: ViewHints }
  | { kind: "people"; rows: Row[]; hints: ViewHints }
  | { kind: "documents"; rows: Row[]; hints: ViewHints }
  | { kind: "views"; views: ViewBlock[]; answer: string }
  | { kind: "block"; block: ViewBlock }
  | { kind: "empty" };

const isObj = (v: unknown): v is Row => !!v && typeof v === "object" && !Array.isArray(v);

/** The record's fields other sections draw (and the system's own `meta`), left out of the overview. */
function partFields(ws: Required<RecordWorkspace>, hints: ViewHints): Set<string> {
  const out = new Set<string>(["meta"]);
  for (const s of ws.sections) if (s.source.startsWith("part:")) out.add(s.source.slice(5).split(".")[0] as string);
  for (const p of [hints.rows, hints.geo && "geojson" in hints.geo ? hints.geo.geojson : undefined, hints.network?.nodes, hints.network?.edges]) if (p) out.add(p.split(".")[0] as string);
  return out;
}

function guessKind(data: unknown, field: string, hints: ViewHints): Exclude<SectionKind, "fields" | "views"> {
  if (hints.rows === field && hints.time) return "timeline";
  if (isObj(data) && (data.type === "FeatureCollection" || data.type === "Feature")) return "map";
  if (isObj(data) && Array.isArray(data.nodes) && Array.isArray(data.edges)) return "network";
  return "table";
}

/** One section's data, from the lookups that came back. Pure: the page draws whatever this says. */
export function sectionData(section: WorkspaceSection, ws: Required<RecordWorkspace>, parts: Record<string, LookupPart | undefined>): SectionData {
  if (section.source.startsWith("related:")) {
    const part = parts[section.source];
    if (!part || (!part.views.length && !part.answer.trim())) return { kind: "empty" };
    return { kind: "views", views: part.views, answer: part.answer };
  }
  const { record, hints } = recordFrom(parts.lookup);
  if (section.source === "lookup") {
    if (section.kind === "views") {
      const part = parts.lookup;
      return part && (part.views.length || part.answer.trim()) ? { kind: "views", views: part.views, answer: part.answer } : { kind: "empty" };
    }
    if (!Object.keys(record).length) return { kind: "empty" };
    const skip = partFields(ws, hints);
    return { kind: "fields", record: Object.fromEntries(Object.entries(record).filter(([k]) => !skip.has(k))), hints };
  }
  const field = section.source.slice(5);
  const data = pick(record, field);
  if (data === undefined || data === null || (Array.isArray(data) && !data.length)) return { kind: "empty" };
  const kind = section.kind && section.kind !== "views" && section.kind !== "fields" ? section.kind : guessKind(data, field, hints);
  const shared: ViewHints = { ...(hints.entityFields ? { entityFields: hints.entityFields } : {}), ...(hints.status ? { status: hints.status } : {}) };
  if (kind === "people" || kind === "documents") {
    const rows = rowsOf(data);
    return rows.length ? { kind, rows, hints: shared } : { kind: "empty" };
  }
  const viewKind: ViewKind = kind;
  const own: ViewHints = { ...shared, title: section.label };
  if (viewKind === "timeline" && hints.rows === field && hints.time) own.time = hints.time;
  if (section.open) own.entityFields = { ...(own.entityFields ?? {}), [section.open.field]: { type: section.open.type } };
  return { kind: "block", block: { type: "view", kind: viewKind, hints: own, data } };
}

// ------------------------------------------------------------------------------ the header

export interface RecordHeader {
  title: string;
  status: string | null;
  /** A few plain facts under the title (field, value). */
  facts: { field: string; value: string }[];
}

const isText = (v: unknown): v is { en?: string; ar?: string } => isObj(v) && Object.keys(v).length > 0 && Object.keys(v).every((k) => k === "en" || k === "ar");
const scalarText = (v: unknown, lang: "ar" | "en"): string | null => {
  if (typeof v === "string") return v.trim() ? v : null;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (isText(v)) return (lang === "en" ? v.en || v.ar : v.ar || v.en) ?? null;
  return null;
};

/** The workspace header: the record's title, its status and up to `max` short facts. */
export function recordHeader(record: Row, hints: ViewHints, type: RecordType, key: string, lang: "ar" | "en", max = 4): RecordHeader {
  const titleField = ["title", "name", "full_name", lang === "ar" ? "name_ar" : "name_en", "label"].find((k) => scalarText(record[k], lang));
  const title = (titleField && scalarText(record[titleField], lang)) || key;
  const statusField = hints.status?.field ?? ("status" in record ? "status" : null);
  const status = statusField ? scalarText(record[statusField], lang) : null;
  const facts: RecordHeader["facts"] = [];
  for (const [field, v] of Object.entries(record)) {
    if (facts.length >= max) break;
    if (field === titleField || field === statusField || field === type.key || field === "summary" || field.startsWith("name_")) continue;
    const text = typeof v === "object" ? null : scalarText(v, lang);
    if (text && text.length <= 80) facts.push({ field, value: text });
  }
  return { title, status, facts };
}

/** A written summary the record carries in the reader's language (`summary: {en, ar}`), if any. */
export function recordSummary(record: Row, lang: "ar" | "en"): string | null {
  const v = record.summary ?? record.description;
  return scalarText(v, lang);
}

// ----------------------------------------------------------------------------- analyses

export interface PurposeInput {
  reason: string;
  note?: string;
  record: { type: string; key: string };
}

/** The request an analysis starts: its agent, the record's key and the reason for access. */
export function analysisRequest(a: WorkspaceAnalysis, ctx: { type: RecordType; key: string; record: Row; user: Record<string, unknown> }, lang: "ar" | "en", purpose?: PurposeInput | null, question?: string): { agent: string; input: Record<string, unknown>; question: string } {
  const scalars = Object.fromEntries(Object.entries(ctx.record).filter(([, v]) => v === null || typeof v !== "object"));
  const recordCtx = { ...scalars, key: ctx.key, type: ctx.type.type };
  const filled = fillPlaceholders(a.input ?? { [ctx.type.key]: "{record.key}" }, { user: ctx.user, record: recordCtx });
  const words = question?.trim() || (a.question ? fillPlaceholders((lang === "en" ? a.question.en : a.question.ar) || a.question.en, { record: recordCtx }) : `${(lang === "en" ? a.label.en : a.label.ar) || a.label.en} ${ctx.key}`);
  return { agent: a.agent, input: { ...filled, ...(purpose ? { purpose } : {}) }, question: words };
}

// ---------------------------------------------------------------------- purpose of access

/** The reasons offered (i18n `officer:purpose.reasons.<id>`); `other` needs words of its own. */
export const PURPOSE_REASONS = ["investigation", "court", "detention", "review", "citizen_request", "other"] as const;
export type PurposeReason = (typeof PURPOSE_REASONS)[number];
export const PURPOSE_NOTE_MAX = 300;

export function purposeValid(reason: string, note: string): boolean {
  if (!(PURPOSE_REASONS as readonly string[]).includes(reason)) return false;
  if (note.length > PURPOSE_NOTE_MAX) return false;
  return reason !== "other" || note.trim().length >= 5;
}

export function purposeRequired(profile: CommandProfile | null, type: string): boolean {
  return !!recordType(profile, type)?.purpose_required;
}

/** Where reasons are kept for the session: `sessionStorage` (one tab, gone when it closes), else memory. */
export interface PurposeStore {
  get(k: string): string | null;
  set(k: string, v: string): void;
}

const memory = new Map<string, string>();
export const memoryPurposeStore: PurposeStore = { get: (k) => memory.get(k) ?? null, set: (k, v) => void memory.set(k, v) };

export function sessionPurposeStore(): PurposeStore {
  return {
    get: (k) => {
      try {
        return globalThis.sessionStorage?.getItem(k) ?? memory.get(k) ?? null;
      } catch {
        return memory.get(k) ?? null;
      }
    },
    set: (k, v) => {
      memory.set(k, v);
      try {
        globalThis.sessionStorage?.setItem(k, v);
      } catch {
        /* storage unavailable: kept for this page only */
      }
    },
  };
}

const purposeKey = (profile: string, type: string, key: string) => `ap.record.purpose.${profile}.${type}.${key}`;

/** The reason given for this record in this session, or null (then it must be asked). */
export function givenPurpose(store: PurposeStore, profile: string, type: string, key: string): PurposeInput | null {
  const raw = store.get(purposeKey(profile, type, key));
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as { reason?: unknown; note?: unknown };
    if (typeof p.reason !== "string" || !(PURPOSE_REASONS as readonly string[]).includes(p.reason)) return null;
    const note = typeof p.note === "string" ? p.note.slice(0, PURPOSE_NOTE_MAX) : "";
    return { reason: p.reason, ...(note ? { note } : {}), record: { type, key } };
  } catch {
    return null;
  }
}

export function rememberPurpose(store: PurposeStore, profile: string, type: string, key: string, reason: string, note: string): PurposeInput {
  const clean = note.trim().slice(0, PURPOSE_NOTE_MAX);
  store.set(purposeKey(profile, type, key), JSON.stringify({ reason, note: clean }));
  return { reason, ...(clean ? { note: clean } : {}), record: { type, key } };
}

/** The workspace's address for a record. */
export function workspacePath(type: string, key: string): string {
  return `/records/${encodeURIComponent(type)}/${encodeURIComponent(key)}`;
}
